import React, { useState, useEffect } from 'react';
import PuenteApi from '../../../../../pageauth/PuenteApi';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Pagination from 'react-bootstrap/Pagination';
import { FaPlus, FaSearch, FaFilter, FaEdit, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import Cargando from '../../../../../Cargando';
import ProveedorComprobanteModal from './ProveedorComprobanteModal';

export const RW_Proveedores_Comprobantes = ({ estaciones, proveedores, razonesSociales }) => {
    const { ObtenerComprobantesProveedor, EliminarComprobanteProveedor } = PuenteApi();
    const { StartCargando, StopCargando } = Cargando();

    const [comprobantes, setComprobantes] = useState([]);
    const [pagination, setPagination] = useState({});

    const [showModal, setShowModal] = useState(false);
    const [comprobanteAEditar, setComprobanteAEditar] = useState(null);

    // Filters
    const [filtroEstacion, setFiltroEstacion] = useState("");
    const [filtroProveedor, setFiltroProveedor] = useState("");
    const [filtroRazonSocial, setFiltroRazonSocial] = useState("");
    const [filtroFechaDesde, setFiltroFechaDesde] = useState("");
    const [filtroFechaHasta, setFiltroFechaHasta] = useState("");

    useEffect(() => {
        cargarComprobantes();
    }, [filtroEstacion, filtroProveedor, filtroRazonSocial, filtroFechaDesde, filtroFechaHasta]);

    const cargarComprobantes = async (page = 1) => {
        StartCargando("Cargando comprobantes...");

        // Find endpoint based on selected station filter or default to first/local
        let endpoint = null;
        if (filtroEstacion) {
            const estacion = estaciones.find(e => e.id.toString() === filtroEstacion);
            endpoint = estacion ? estacion.endpoint : null;
        } else {
            // For now, we need an endpoint to query. If no station selected, query the local one or first one?
            // Since the data is in the DB, any working endpoint should technically work if we are querying the shared DB.
            // But usually we want to query the specific station context.
            // Let's use the local station or first one as default if no filter.
            endpoint = estaciones.find(e => e.local)?.endpoint || estaciones[0]?.endpoint;
        }

        if (!endpoint) {
            StopCargando();
            return;
        }

        const filters = {
            page,
            razon_social_id: filtroRazonSocial,
            proveedor_id: filtroProveedor,
            fecha_desde: filtroFechaDesde,
            fecha_hasta: filtroFechaHasta
        };

        const response = await ObtenerComprobantesProveedor(endpoint, filters);

        if (response && response.data) {
            setComprobantes(response.data);
            setPagination({
                current_page: response.current_page,
                last_page: response.last_page,
                total: response.total
            });
        }
        StopCargando();
    };

    const obtenerEndpointActual = () => {
        if (filtroEstacion) {
            const estacion = estaciones.find(e => e.id.toString() === filtroEstacion);
            return estacion ? estacion.endpoint : null;
        } else {
            return estaciones.find(e => e.local)?.endpoint || estaciones[0]?.endpoint;
        }
    }

    const handleNuevoComprobante = () => {
        setComprobanteAEditar(null);
        setShowModal(true);
    };

    const handleEditar = (comprobante) => {
        setComprobanteAEditar(comprobante);
        setShowModal(true);
    };

    const handleEliminar = (id) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: "No podrás revertir esto!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, eliminar!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                StartCargando("Eliminando...");
                const endpoint = obtenerEndpointActual();
                if (endpoint) {
                    const success = await EliminarComprobanteProveedor(endpoint, id);
                    if (success) {
                        Swal.fire(
                            'Eliminado!',
                            'El comprobante ha sido eliminado.',
                            'success'
                        );
                        cargarComprobantes(pagination.current_page);
                    }
                }
                StopCargando();
            }
        });
    };

    const handleSave = () => {
        cargarComprobantes(pagination.current_page);
    };

    return (
        <div className="card shadow-sm p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="text-primary mb-0">Gestión de Comprobantes</h4>
                <Button variant="success" onClick={handleNuevoComprobante}>
                    <FaPlus className="me-2" /> Nuevo Comprobante
                </Button>
            </div>

            {/* Filters */}
            <div className="row mb-4 bg-light p-3 rounded">
                <div className="col-md-3 mb-2">
                    <Form.Label>Estación</Form.Label>
                    <Form.Control
                        as="select"
                        value={filtroEstacion}
                        onChange={e => setFiltroEstacion(e.target.value)}
                    >
                        <option value="">Todas</option>
                        {estaciones.map(e => (
                            <option key={e.id} value={e.id}>{e.nombre}</option>
                        ))}
                    </Form.Control>
                </div>
                <div className="col-md-3 mb-2">
                    <Form.Label>Razón Social</Form.Label>
                    <Form.Control
                        as="select"
                        value={filtroRazonSocial}
                        onChange={e => setFiltroRazonSocial(e.target.value)}
                    >
                        <option value="">Todas</option>
                        {razonesSociales && razonesSociales.map(r => (
                            <option key={r.id} value={r.id}>{r.razon_social}</option>
                        ))}
                    </Form.Control>
                </div>
                <div className="col-md-3 mb-2">
                    <Form.Label>Proveedor</Form.Label>
                    <Form.Control
                        as="select"
                        value={filtroProveedor}
                        onChange={e => setFiltroProveedor(e.target.value)}
                    >
                        <option value="">Todos</option>
                        {proveedores && proveedores.map(p => (
                            <option key={p.id} value={p.id}>{p.razon_social}</option>
                        ))}
                    </Form.Control>
                </div>
                <div className="col-md-3 mb-2 row">
                    <div className="col-6">
                        <Form.Label>Desde</Form.Label>
                        <Form.Control
                            type="date"
                            value={filtroFechaDesde}
                            onChange={e => setFiltroFechaDesde(e.target.value)}
                        />
                    </div>
                    <div className="col-6">
                        <Form.Label>Hasta</Form.Label>
                        <Form.Control
                            type="date"
                            value={filtroFechaHasta}
                            onChange={e => setFiltroFechaHasta(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Table */}
            <Table striped bordered hover responsive>
                <thead className="bg-light">
                    <tr>
                        <th>Fecha Emisión</th>
                        <th>Tipo</th>
                        <th>Número</th>
                        <th>Razón Social</th>
                        <th>Proveedor</th>
                        <th className="text-end">Total</th>
                        <th>Observación</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {comprobantes.length > 0 ? (
                        comprobantes.map(comp => (
                            <tr key={comp.id}>
                                <td>{new Date(comp.fechaEmision).toLocaleDateString()}</td>
                                <td>{comp.tipo} - {comp.letra}</td>
                                <td>{comp.puntoventa}-{comp.numero}</td>
                                <td>{comp.razon_social ? comp.razon_social.razon_social : '-'}</td>
                                <td>{comp.proveedor ? comp.proveedor.razon_social : '-'}</td>
                                <td className="text-end fw-bold">${Number(comp.total).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                                <td>{comp.observacion}</td>
                                <td>
                                    <Button variant="warning" size="sm" className="me-2" onClick={() => handleEditar(comp)}>
                                        <FaEdit />
                                    </Button>
                                    <Button variant="danger" size="sm" onClick={() => handleEliminar(comp.id)}>
                                        <FaTrash />
                                    </Button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="8" className="text-center text-muted">No hay comprobantes para mostrar.</td>
                        </tr>
                    )}
                </tbody>
            </Table>

            {/* Pagination */}
            {pagination.last_page > 1 && (
                <div className="d-flex justify-content-center mt-3">
                    <Pagination>
                        <Pagination.First onClick={() => cargarComprobantes(1)} disabled={pagination.current_page === 1} />
                        <Pagination.Prev onClick={() => cargarComprobantes(pagination.current_page - 1)} disabled={pagination.current_page === 1} />
                        {[...Array(pagination.last_page)].map((_, i) => (
                            <Pagination.Item key={i + 1} active={i + 1 === pagination.current_page} onClick={() => cargarComprobantes(i + 1)}>
                                {i + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next onClick={() => cargarComprobantes(pagination.current_page + 1)} disabled={pagination.current_page === pagination.last_page} />
                        <Pagination.Last onClick={() => cargarComprobantes(pagination.last_page)} disabled={pagination.current_page === pagination.last_page} />
                    </Pagination>
                </div>
            )}

            {/* Modal */}
            <ProveedorComprobanteModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                comprobante={comprobanteAEditar}
                estacion={estaciones.find(e => e.id.toString() === filtroEstacion) || estaciones.find(e => e.local) || estaciones[0]}
                proveedores={proveedores}
                razonesSociales={razonesSociales}
                onSave={handleSave}
            />
        </div>
    );
};
